/**
 * 喫煙所マップ物件ストア (Pinia)。
 *
 * 避難マップ (propertyStore) / 災害時避難先 (propertyStoreV2) とは別の
 * localStorage キーで独立管理する。永続化方式は Firebase 設定 (§4.3) の有無で
 * 分岐する (3ストア共通):
 * - 未設定 (isFirebaseConfigured()===false): localStorage (persisted-crud)。
 * - 設定済み: Firestore (firestore-crud)。auth (§7) の status が 'ready' になるまで
 *   crud を生成しない。
 * 同梱シード5件 (京都/神戸/さいたま/千葉/仙台) を持つ (V2 と同じ方式)。
 *
 * 編集は全て updateProperty 経由で property を直接 mutation しないため、updatedAt は
 * crud.update が更新する — v2 のような touch() は不要。
 */
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch, type Ref } from 'vue';
import type { DraftSmokingProperty, SmokingProperty } from '@/types/smoking';
import seedData from '@/data/smoking-seeds.json';
import { generateId, nowIso } from '@/lib/id';
import { createPersistedCrud, type PersistedCrud } from '@/lib/stores/persisted-crud';
import { createFirestoreCrud, type FirestoreCrud } from '@/lib/stores/firestore-crud';
import { isFirebaseConfigured } from '@/lib/firebase/init';
import { ensureSeedsSmoking } from '@/lib/firebase/bootstrap';
import { useAuthStore } from '@/stores/authStore';
import { currentEditor, withEditStamp } from './edit-stamp';
import type { SaveState } from '@/lib/firebase/save-pipeline';

const STORAGE_KEY = 'evacuation-map.properties.smoking';

/** プロジェクト同梱の既定物件。生成: scripts/bake-seeds.mjs */
const SEED_PROPERTIES = seedData as unknown as SmokingProperty[];

/** 既定物件のディープコピー (imported module を書き換えないため)。 */
function cloneSeed(): SmokingProperty[] {
  return SEED_PROPERTIES.map(
    (p) => JSON.parse(JSON.stringify(p)) as SmokingProperty,
  );
}

/**
 * ストアが実際に生成済みかどうか (ログアウト flush 用)。未訪問のままログアウト
 * した場合に、flush のためだけに Firestore 初期化を走らせない (signOut と競合させない)。
 */
let storeInstantiated = false;

/**
 * src の値を dst へ即時反映する小ヘルパ (immediate)。別ユーザー再ログイン時に
 * crud を張り替えるため停止関数を返す (再バインド前に必ず呼ぶこと)。
 */
function watchBinding<S, D extends S>(src: Ref<D>, dst: Ref<S>): () => void {
  return watch(
    src,
    (v) => {
      dst.value = v;
    },
    { immediate: true },
  );
}

/**
 * ストア未生成なら何もしない、生成済みなら flush する。ログアウト時に未訪問の
 * 喫煙所画面によって Firestore を初期化せず、signOut との競合を回避する。
 */
export async function flushSmokingStoreIfActive(): Promise<void> {
  if (!storeInstantiated) return;
  const store = useSmokingStore();
  await store.flushAll();
}

export const useSmokingStore = defineStore('smoking', () => {
  // ストア生成時にフラグをセット (defineStore は一度だけ実行される)
  storeInstantiated = true;

  const firestoreMode = isFirebaseConfigured();
  const crud = shallowRef<
    PersistedCrud<SmokingProperty> | FirestoreCrud<SmokingProperty> | undefined
  >(undefined);
  const properties = computed(() => crud.value?.items.value ?? []);
  const ready = ref(false);
  const saveState = ref<SaveState | 'local'>('local');

  function getProperty(id: string): SmokingProperty | undefined {
    return crud.value?.get(id);
  }

  /**
   * 新規作成直後の「初回表示で地図を内容に自動フィット」対象の物件 id
   * (transient、localStorage 非永続化。v1 propertyStore と同じ方式)。
   * createProperty で立て、SmokingEditor が地図の準備完了後に consumeInitialFit で
   * 一度だけ消費する。リロード・既存物件では発火しない = 保存済み mapState
   * (ユーザーの手動フレーミング) を上書きしない。
   */
  const needsInitialFit = new Set<string>();

  /** 初回自動フィットのフラグを消費する (立っていれば降ろして true)。 */
  function consumeInitialFit(propertyId: string): boolean {
    return needsInitialFit.delete(propertyId);
  }

  function createProperty(draft: DraftSmokingProperty): SmokingProperty {
    const property: SmokingProperty = {
      ...draft,
      id: generateId('smoking'),
      createdAt: nowIso(),
      updatedAt: nowIso(),
      updatedBy: currentEditor(),
    };
    crud.value?.insert(property);
    needsInitialFit.add(property.id);
    return property;
  }

  function updateProperty(
    id: string,
    patch: Partial<Omit<SmokingProperty, 'id' | 'createdAt'>>,
  ): void {
    // updatedAt は crud.update が現在時刻へ更新する。updatedBy は withEditStamp。
    crud.value?.update(id, withEditStamp(patch));
  }

  function deleteProperty(id: string): void {
    crud.value?.remove(id);
  }

  function flushAll(): Promise<void> {
    const c = crud.value;
    return c && 'flushAll' in c ? c.flushAll() : Promise.resolve();
  }

  /**
   * 既定物件を再投入する (idごとに上書き; ユーザーが追加した他物件は保持)。
   * Firestore モードではシード再投入は管理者操作のみ (§6-5) のため no-op。
   */
  function resetToSeed(): void {
    if (firestoreMode) {
      console.warn(
        '[smokingStore] resetToSeed は Firestore モードでは no-op です (シード再投入は管理者操作のみ)',
      );
      return;
    }
    for (const seed of cloneSeed()) {
      const idx = properties.value.findIndex((p) => p.id === seed.id);
      if (idx < 0) crud.value?.insert(seed);
      else properties.value[idx] = seed;
    }
  }

  if (firestoreMode) {
    // ---- Firestore モード (§4.3) — propertyStoreV2 と同型 ----
    const auth = useAuthStore();

    /** 現在の crud がどの email にバインドされているか (別ユーザー再ログイン検知用)。 */
    let boundEmail: string | null = null;
    /** ready/saveState の watchBinding 停止関数 (再バインド前に解除する)。 */
    let stopBindings: Array<() => void> = [];
    /** 現在の fsCrud の dispose (次のユーザーへ張り替える際に呼ぶ)。 */
    let disposeCurrent: (() => void) | null = null;

    const initFirestore = (email: string, displayName: string | null): void => {
      for (const stop of stopBindings) stop();
      stopBindings = [];
      const fsCrud = createFirestoreCrud<SmokingProperty>({
        kind: 'smoking_properties',
        email,
        displayName,
        deferLoad: true,
      });
      crud.value = fsCrud;
      stopBindings.push(watchBinding(fsCrud.ready, ready));
      stopBindings.push(watchBinding(fsCrud.saveState, saveState));
      disposeCurrent = fsCrud.dispose;
      boundEmail = email;
      void (async () => {
        try {
          await ensureSeedsSmoking(email, displayName); // §6-5 フラグdoc + transaction
        } catch (e) {
          // シード注入の失敗 (transaction競合/瞬断) で閲覧まで道連れにしない。
          console.warn('[smokingStore] シード注入に失敗 (閲覧は継続)', e);
        }
        fsCrud.startLoad();
      })();
    };

    // auth.status の遷移を継続監視する (ログアウト→別ユーザー再ログインで張り替え)。
    watch(
      () => auth.status,
      (status) => {
        if (status === 'ready' && auth.email && auth.email !== boundEmail) {
          disposeCurrent?.();
          initFirestore(auth.email, auth.displayName);
        } else if (status !== 'ready') {
          boundEmail = null;
          ready.value = false;
        }
      },
      { immediate: true },
    );
  } else {
    // ---- localStorage モード (現行どおり) ----
    // 初回は initial の結果を即保存し、以降は編集内容 (削除含む) を優先する。
    const firstRun = localStorage.getItem(STORAGE_KEY) === null;
    crud.value = createPersistedCrud<SmokingProperty>({
      storageKey: STORAGE_KEY,
      label: '物件データ(喫煙所)',
      initial: cloneSeed, // 初回のみ既定物件
    });
    if (firstRun) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(properties.value));
      } catch (e) {
        console.warn('既定物件(喫煙所)の初期保存に失敗しました', e);
      }
    }
    ready.value = true;
  }

  return {
    properties,
    ready,
    saveState,
    flushAll,
    getProperty,
    createProperty,
    updateProperty,
    deleteProperty,
    resetToSeed,
    consumeInitialFit,
  };
});
